import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TextLinkProps {
    href: string
    children: ReactNode
    className?: string
    arrow?: boolean
    mono?: boolean
}

const isExternal = (href: string): boolean => /^(https?:)?\/\//.test(href)

/**
 * Inline link with a quiet underline that tightens on hover.
 * External links open in a new tab and show a diagonal arrow.
 */
const TextLink: FC<TextLinkProps> = ({ href, children, className, arrow = false, mono = false }): ReactNode => {
    const external = isExternal(href)
    const classes = cn(
        'group/link inline-flex items-center gap-1.5 text-foreground link-underline hover:text-brand',
        mono && 'font-mono text-caption uppercase tracking-label',
        className
    )

    const icon = arrow && (external
        ? <ArrowUpRight aria-hidden='true' className='size-3.5 shrink-0 transition-transform duration-300 ease-editorial group-hover/link:-translate-y-0.5 group-hover/link:translate-x-0.5' />
        : <ArrowRight aria-hidden='true' className='size-3.5 shrink-0 transition-transform duration-300 ease-editorial group-hover/link:translate-x-0.5' />)

    if (external || href.startsWith('mailto:') || href.endsWith('.xml')) {
        return (
            <a
                href={href}
                className={classes}
                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
                <span>{children}</span>
                {icon}
            </a>
        )
    }

    return (
        <Link href={href} className={classes}>
            <span>{children}</span>
            {icon}
        </Link>
    )
}

export default TextLink
